"use client";

import { useState } from "react";
import {
  MoreHorizontal,
  Download,
  Eye,
  Filter,
  DollarSign,
  TrendingUp,
  XCircle,
  Clock,
  CheckCircle,
} from "lucide-react";
import { Button } from "../UI/Button";
import { Input } from "../UI/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../UI/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../UI/dropdown-menu";
import { Badge } from "../UI/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../UI/tabs";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import Pagination from "../../utils/pagination";


export default function SimpleBillingTable({
  myStatistics,
  data,
  filter,
  setFilter,
  me,
}: any) {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("income");

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completado
          </Badge>
        );
      case "pending":
      case "processing":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            <Clock className="w-3 h-3 mr-1" />
            Pendiente
          </Badge>
        );
      case "refunded":
      case "cancelled":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            {status === "refunded" ? "Reembolsado" : "Cancelado"}
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };


  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 sm:p-6">
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Ingresos Totales</h4>
            <DollarSign className="h-4 w-4 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-green-600">
            ${myStatistics[0].ingresos.toLocaleString()}
          </div>
          <p className="text-xs text-gray-500">
            {myStatistics[0].reservas} transacciones • $
            {myStatistics[0].comisiones.toLocaleString()} comisiones
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Pendientes</h4>
            <Clock className="h-4 w-4 text-yellow-600" />
          </div>
          <div className="text-2xl font-bold text-yellow-600">
            ${myStatistics[1].ingresos.toLocaleString()}
          </div>
          <p className="text-xs text-gray-500">
            {myStatistics[1].reservas} transacciones • $
            {myStatistics[1].comisiones.toLocaleString()} comisiones
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Canceladas</h4>
            <XCircle className="h-4 w-4 text-red-600" />
          </div>
          <div className="text-2xl font-bold text-red-600">
            ${myStatistics[2].ingresos.toLocaleString()}
          </div>
          <p className="text-xs text-gray-500">
            {myStatistics[2].reservas} transacciones • $
            {myStatistics[2].comisiones} comisiones
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="px-4 sm:px-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger
              value="income"
              className="text-xs sm:text-sm"
              onClick={() =>
                setFilter({
                  ...filter,
                  status: "approved",
                })
              }
            >
              <TrendingUp className="w-4 h-4 mr-1" />
              Ingresos ({myStatistics[0].reservas})
            </TabsTrigger>
            <TabsTrigger
              value="pending"
              className="text-xs sm:text-sm"
              onClick={() =>
                setFilter({
                  ...filter,
                  status: "pending",
                })
              }
            >
              <Clock className="w-4 h-4 mr-1" />
              Pendientes ({myStatistics[1].reservas})
            </TabsTrigger>
            <TabsTrigger
              value="cancelled"
              className="text-xs sm:text-sm"
              onClick={() =>
                setFilter({
                  ...filter,
                  status: "cancel",
                })
              }
            >
              <XCircle className="w-4 h-4 mr-1" />
              Canceladas ({myStatistics[2].reservas})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={activeTab} className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 px-4 sm:px-6">
            <div className="flex-1">
              <Input
                placeholder="Buscar transacciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="icon">
                <Filter className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon">
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-md border overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transacción</TableHead>
                    {me?.role == 'admin' ? <TableHead className="w-[200px]">Anfitrion</TableHead> : ''}
                    <TableHead>Cliente</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Monto Total</TableHead>
                    <TableHead>Comisión</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Check-In</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((item: any) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {item.transaction_id}
                          </div>
                          <div className="text-sm text-gray-500">
                            Reserva #{item.id}
                          </div>
                        </div>
                      </TableCell>
                      {me?.role==='admin'? <TableCell>
                        <div className="font-medium">{`${item?.seller[0]?.firstname} ${item.seller[0]?.lastname}`}</div>
                      </TableCell>: ''}
                      <TableCell>
                        <div className="font-medium">{`${item.firstname} ${item.lastname}`}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {item.property_id ? item.property[0].title : ""}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-bold">
                          ${item.price.toFixed(2)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-green-600">
                          ${item.executed.toFixed(2)}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {format(new Date(item.start_date), "dd MMM", {
                            locale: es,
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">Abrir menú</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                            <DropdownMenuItem>
                              <Eye className="mr-2 h-4 w-4" />
                              Ver detalles
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Download className="mr-2 h-4 w-4" />
                              Descargar factura
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination */}
          <Pagination data={data} setPage={setFilter} page={filter} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
